/**
 * Report configuration for eds-data-ux which contains:
 *   - Which reports exist and their metadata (path, label, nav category)
 *   - Which reports are available per product
 *
 * To add a new report: add an entry to REPORTS and update PRODUCT_REPORTS.
 * To add a new product: add an entry to PRODUCT_REPORTS (or to
 *   MAGAZINE_PRODUCT_CODES for overview-only magazine products).
 *
 * LaunchDarkly flags can be layered on top of this config in the layout
 * component to gate individual reports during gradual rollouts.
 */

export type ReportPath =
  | 'overview'
  | 'engagement'
  | 'lexile-measure'
  | 'ge-score';

export type ReportCategory = 'Usage' | 'Engagement' | 'Assessments';

export interface ReportConfig {
  /** URL path segment under /data/:productId/ — also used as the unique key */
  path: ReportPath;
  /** Display label used in navigation */
  label: string;
  /** Nav grouping category (UI only, not in URL) */
  category: ReportCategory;
}

export const REPORTS: ReportConfig[] = [
  {
    path: 'overview',
    label: 'Overview',
    category: 'Usage',
  },
  {
    path: 'engagement',
    label: 'Engagement Feed',
    category: 'Engagement',
  },
  {
    path: 'lexile-measure',
    label: 'Lexile® Measure',
    category: 'Assessments',
  },
  {
    path: 'ge-score',
    label: 'GE Score',
    category: 'Assessments',
  },
];

/**
 * All magazine (CM group) product codes — overview report only.
 * To add a new magazine product, simply add its code to this set.
 */
const MAGAZINE_CODES = [
  'junior',
  'action',
  'letsfindout',
  'math',
  'mybigworld',
  'sciencespin2',
  'sciencespin36',
  'scienceworld',
  'scope',
  'sn1',
  'storyworks1',
  'art',
  'choices',
  'sciencespink1',
  'sn2',
  'sn3',
  'sn4',
  'sn56',
  'storyworks',
  'storyworksjr',
  'upfront',
  'ahora',
  'cheznous',
  'quetal',
  'storyworks2',
  'superstem',
  'allonsy',
  'bonjour',
  'cava',
  'elsol',
] as const;

export const MAGAZINE_PRODUCT_CODES = new Set<string>(MAGAZINE_CODES);

const MAGAZINE_REPORTS: ReportPath[] = ['overview'];

/**
 * Per-product report configuration for non-magazine products.
 * Magazine products are handled via MAGAZINE_PRODUCT_CODES above.
 */
const PRODUCT_REPORTS_INTERNAL = {
  WORD: ['overview', 'engagement'],
  FIRST: ['overview', 'engagement'],
  LITPRO: ['overview', 'engagement', 'lexile-measure'],
  R4R: ['overview', 'engagement', 'ge-score'],
  rise: ['overview'],
} as const satisfies Record<string, readonly ReportPath[]>;

export const PRODUCT_REPORTS: Record<string, readonly ReportPath[]> =
  PRODUCT_REPORTS_INTERNAL;

/**
 * Product-code-level report exclusions.
 *
 * For a given applicationCode, maps a ReportPath to the set of product codes
 * that, when they are the ONLY products the user holds for that app, cause the
 * report to be hidden.
 *
 * Example: if a user's R4R products consist solely of codes listed under
 * 'ge-score', the GE Score report will not be shown.
 */
export const REPORT_PRODUCT_CODE_EXCLUSIONS: Record<
  string,
  Partial<Record<ReportPath, string[]>>
> = {
  R4R: {
    'ge-score': ['R4RWB'],
  },
};

// ── Product Code Types ─────────────────────────────────────────────────────────

/** Non-magazine product codes (WORD, FIRST, LITPRO, R4R). */
export type NonMagazineProductCode = keyof typeof PRODUCT_REPORTS_INTERNAL;

/** Magazine product codes. */
export type MagazineProductCode = (typeof MAGAZINE_CODES)[number];

/** All supported product codes for the data dashboard. */
export type ProductCode = NonMagazineProductCode | MagazineProductCode;

/**
 * Non-magazine product codes — derived from PRODUCT_REPORTS keys.
 * Use this wherever you need to distinguish full-featured products from magazines.
 */
export const NON_MAGAZINE_PRODUCTS: NonMagazineProductCode[] = Object.keys(
  PRODUCT_REPORTS_INTERNAL
) as NonMagazineProductCode[];

/**
 * Returns true if the given product code is a MagazineProductCode.
 */
export function isMagazineEdApp(
  edApp: ProductCode
): edApp is MagazineProductCode {
  return MAGAZINE_PRODUCT_CODES.has(edApp);
}

/**
 * Returns true if the product code is supported for the data dashboard.
 */
export function isSupportedProduct(productCode: string): boolean {
  return (
    productCode in PRODUCT_REPORTS || MAGAZINE_PRODUCT_CODES.has(productCode)
  );
}

/**
 * Returns the ReportConfig objects available for a given product.
 * Returns an empty array if the product is unknown.
 *
 * @param productId - The application code (e.g. 'R4R', 'LITPRO').
 * @param userProductCodes - Optional array of the user's product codes for this
 *   application. When provided, product-code-level exclusions from
 *   REPORT_PRODUCT_CODE_EXCLUSIONS are applied.
 */
export function getReportsForProduct(
  productId: string,
  userProductCodes?: string[]
): ReportConfig[] {
  const allowed =
    PRODUCT_REPORTS[productId] ??
    (MAGAZINE_PRODUCT_CODES.has(productId) ? MAGAZINE_REPORTS : []);

  let reports = REPORTS.filter((r) => allowed.includes(r.path));

  // Apply product-code-level exclusions when user product codes are known
  const exclusions = REPORT_PRODUCT_CODE_EXCLUSIONS[productId];
  if (userProductCodes && userProductCodes.length > 0 && exclusions) {
    reports = reports.filter((r) => {
      const excludedByProducts = exclusions[r.path];
      if (!excludedByProducts) return true;
      // Hide the report only if ALL of the user's product codes for this app
      // are within the exclusion list
      const hasOnlyExcludedProducts = userProductCodes.every((code) =>
        excludedByProducts.includes(code)
      );
      return !hasOnlyExcludedProducts;
    });
  }

  return reports;
}

/**
 * Returns reports grouped by category, in the order they appear in REPORTS.
 *
 * @param productId - The application code (e.g. 'R4R', 'LITPRO').
 * @param userProductCodes - Optional array of the user's product codes for this
 *   application. Passed through to getReportsForProduct for exclusion logic.
 *
 * @example
 * const grouped = getReportsGroupedByCategory('LitPro');
 * // {
 * //   Usage: [{ path: 'overview', label: 'Overview', ... }],
 * //   Engagement: [{ path: 'engagement', label: 'Engagement Feed', ... }],
 * //   Assessments: [{ path: 'lexile-measure', ... }, { path: 'ge-score',
 * // }
 */
export function getReportsGroupedByCategory(
  productId: string,
  userProductCodes?: string[]
): Partial<Record<ReportCategory, ReportConfig[]>> {
  const reports = getReportsForProduct(productId, userProductCodes);
  return reports.reduce<Partial<Record<ReportCategory, ReportConfig[]>>>(
    (acc, report) => {
      const group = acc[report.category] ?? [];
      return { ...acc, [report.category]: [...group, report] };
    },
    {}
  );
}
